import { useInjectReducer } from "@app/store";
import Button from "@components/Button";
import Checkbox from "@components/Checkbox";
import PageContent from "@components/PageContent";
import PageHeader from "@components/PageHeader";
import SelectAll from "@components/SelectAll";
// import { useLoadAllOfficeByTypeMutation } from "@features/admin/officeManage/officeManageApiSlice";
import {
  useLoadAllCampaignAccessMutation,
  useSaveCampaignAccessMutation,
  useLoadAllOfficeTypeQuery,
} from "@features/admin/campaignManage/campaignManageApiSlice";
import campaignManageSlice, {
  clearSelectedAccess,
  setSelectAll,
  setSelectedRetailers,
  updateRetailerOrder,
  setOneValue,
  clearOne,
  setBulkRetailerOrders,
} from "@features/admin/campaignManage/campaignManageSlice";
import {
  makeSelectOfficeType,
  makeSelectQuery,
  makeSelectSelectedRetailerObj,
  makeSelectSelectedRetailers,
  makeSelectOne,
} from "@features/admin/campaignManage/campaignSelectors";
import { enqueueSnackbar } from "@features/auth/authSlice";
import { APP_NAME } from "@utils/constants";
import { queryHelper } from "@utils/queryHelper";

import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { FaMinus } from "react-icons/fa";
import InputWrapper from "@components/Input";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

const RetailerRowItem = React.memo(({ retailer }) => {
  const dispatch = useDispatch();
  const selectedRetailerObj = useSelector(makeSelectSelectedRetailerObj);

  const selectedData = selectedRetailerObj[retailer._id];
  const [inputValue, setInputValue] = useState(selectedData?.no_of_order ?? "");

  // Enhanced useEffect to sync whenever selectedData changes
  useEffect(() => {
    const newValue = selectedData?.no_of_order ?? "";
    setInputValue(newValue);
  }, [selectedData?.no_of_order, selectedData]); // Added selectedData as dependency

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    dispatch(
      updateRetailerOrder({
        id: retailer._id,
        no_of_order: Number(inputValue),
      })
    );
  };

  const handleCheckboxChange = () => {
    dispatch(
      setSelectedRetailers({
        key: retailer._id,
        Retailer: retailer._id,
        name: retailer.name,
      })
    );
  };

  const isSelected = !!selectedRetailerObj[retailer._id];

  return (
    <div className='flex-1 flex items-center gap-2 border border-gray-300 p-2 rounded bg-white shadow-sm'>
      <Checkbox
        checked={isSelected}
        name={retailer._id}
        onChange={handleCheckboxChange}
      />
      <InputWrapper
        name='no_of_order'
        type='number'
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        divclassname={"w-16"}
      />
      <div className='ml-2 text-sm text-gray-700 flex flex-col sm:flex-row sm:items-center sm:gap-2'>
        <span className='font-bold text-gray-900'>
          ({retailer.office_code})
        </span>
        <span className='text-gray-600'>{retailer.name}</span>
      </div>
    </div>
  );
});

const CampaignAllowAccess = () => {
  useInjectReducer("campaignManage", campaignManageSlice);
  const { scheme_type } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const one = useSelector(makeSelectOne);
  const query = useSelector(makeSelectQuery);
  const selectedRetailers = useSelector(makeSelectSelectedRetailers);
  const selectedRetailerObj = useSelector(makeSelectSelectedRetailerObj);
  const retailerData = useSelector(makeSelectOfficeType);
  const innerRef = useRef();

  const [allOffice, setAllOffice] = useState(false);
  const [applyDefaultOrder, setApplyDefaultOrder] = useState(false);

  const [
    loadAllSchemeAccess,
    {
      data: retailerAccess,
      isLoading: accessLoading,
      isSuccess: accessSuccess,
    },
  ] = useLoadAllCampaignAccessMutation();

  const { data: totalOffice, isLoading: officeLoading } =
    useLoadAllOfficeTypeQuery(
      queryHelper({
        ...query,
        find_type: "RETAILER",
        page: 1,
        size: 0,
      }),
      {
        refetchOnMountOrArgChange: false,
      }
    );

  const [saveSchemeAccess, { isLoading: saveSchemeLoading }] =
    useSaveCampaignAccessMutation();

  useEffect(() => {
    dispatch(clearOne());
    dispatch(clearSelectedAccess());
    loadAllSchemeAccess(id);
    if (retailerAccess?.data && totalOffice) {
      setAllOffice(
        retailerAccess?.data?.allowed_office.length === totalOffice?.data.length
          ? true
          : false
      );
    }
    setApplyDefaultOrder(false);
  }, []);

  useEffect(() => {
    setAllOffice(
      selectedRetailers?.length === totalOffice?.data?.length ? true : false
    );
  }, [selectedRetailers]);

  const handleChangeCheck = (each) => {
    dispatch(
      setSelectedRetailers({
        key: each._id,
        Retailer: each._id,
        name: each.name,
      })
    );
  };

  const handleDefaultValue = (name) => (e) => {
    dispatch(setOneValue({ key: name, value: e.target.value }));
  };

  const handleSelectAll = (allItems) => {
    dispatch(setSelectAll({ allKeys: allItems }));
    setAllOffice(true);
    setApplyDefaultOrder(false);
  };

  const handleGroupSelectAll = (typeKey) => {
    const groupItems = groupedRetailers[typeKey] || [];

    // groupItems.forEach((retailer) => {
    dispatch(
      setSelectedRetailers({
        // key: retailer._id,
        // Retailer: retailer._id,
        // name: retailer.name,
        groupItems,
      })
    );
    // });
  };

  const isSelected = (id) =>
    Object.values(selectedRetailerObj).some((x) => x.id === id);

  const handleSaveSchemeAccess = () => {
    const dataToSave = {
      is_allowed_all_office: allOffice,
      allowed_office: Object.values(selectedRetailerObj).map((retailer) => ({
        office_id: retailer.id,
        no_of_order: retailer.no_of_order,
      })),
    };

    saveSchemeAccess({ body: dataToSave, id })
      .unwrap()
      .then((res) => {
        if (res?.success) {
          dispatch(
            enqueueSnackbar({
              message: res?.msg || "Campaign Access Saved Successfully",
              options: { variant: "success" },
            })
          );
          navigate(`/admin/scheme-manage/data-campaign`);
        }
      })
      .catch((err) => {
        dispatch(
          enqueueSnackbar({
            message: err?.data?.msg || "Campaign access save failed.",
            options: { variant: "warning" },
          })
        );
      });
  };

  const groupRetailersByType = (retailers) => {
    const grouped = {
      CE: [],
      MX: [],
      BOTH: [],
      NONE: [],
    };

    (retailers || []).forEach((retailer) => {
      const types = retailer.retail_type || [];

      if (types.includes("CE") && types.includes("MX")) {
        grouped.BOTH.push(retailer);
      } else if (types.includes("CE")) {
        grouped.CE.push(retailer);
      } else if (types.includes("MX")) {
        grouped.MX.push(retailer);
      } else {
        grouped.NONE.push(retailer);
      }
    });

    return grouped;
  };

  const groupedRetailers = groupRetailersByType(retailerData?.data || []);

  const handleGoBack = () => {
    navigate(`/admin/scheme-manage/data-campaign`);
    dispatch(clearSelectedAccess());
  };

  return (
    <>
      <Helmet>
        <title>{`Retailer Access Manage | ${APP_NAME}`}</title>
      </Helmet>

      <div className='flex items-center justify-between mb-3'>
        <PageHeader
          title={`Retailer Access Manage - ${
            retailerAccess?.data?.name || ""
          } (${retailerAccess?.data?.code || ""})`}
          isBack
          handleGoBack={handleGoBack}
        />
      </div>

      <PageContent>
        <div className='grid grid-cols-5 gap-4 p-4 mb-20'>
          <div className='col-span-5'>
            <SelectAll
              all={retailerData?.data || []}
              chosen_items={selectedRetailers || []}
              selectChange={() => handleSelectAll(retailerData?.data)}
            />
            <Checkbox
              label={
                <span className='text-sm flex'>
                  Default Order{" "}
                  <InputWrapper
                    name='default_order'
                    type='number'
                    value={one.default_order}
                    onChange={handleDefaultValue("default_order")}
                    divclassname={"w-full mb-4"}
                  />
                </span>
              }
              checked={applyDefaultOrder}
              disabled={
                selectedRetailers.length !==
                  (retailerData?.data?.length || 0) || !one.default_order
              }
              onChange={() => {
                const newVal = !applyDefaultOrder;
                setApplyDefaultOrder(newVal);

                dispatch(
                  setBulkRetailerOrders({
                    retailerIds: selectedRetailers,
                    no_of_order: Number(one.default_order),
                    resetToZero: !newVal,
                  })
                );
              }}
              name='defaultOrder'
              className='ml-5'
            />
          </div>

          {["CE", "MX", "BOTH", "NONE"].map(
            (typeKey) =>
              groupedRetailers[typeKey]?.length > 0 && (
                <div key={typeKey} className='col-span-5 border-t pt-4 mb-4'>
                  {/* Group Header */}
                  <div className='flex items-center mb-2'>
                    {(() => {
                      const groupItems = groupedRetailers[typeKey];
                      console.log("groupItems", groupItems);
                      const total = groupItems.length;
                      console.log("total", total);

                      const selectedCount = groupItems.filter((item) =>
                        selectedRetailers.includes(item._id)
                      ).length;
                      console.log("selectedCount", selectedCount);
                      const isIndeterminate =
                        selectedCount > 0 && selectedCount < total;

                      return (
                        <Checkbox
                          label={
                            <span className='font-semibold text-lg capitalize'>
                              {typeKey === "BOTH"
                                ? "CE & MX"
                                : typeKey === "NONE"
                                ? "Others"
                                : typeKey}
                            </span>
                          }
                          checked={selectedCount === total}
                          name={typeKey}
                          onChange={() => handleGroupSelectAll(typeKey)}
                          icon={isIndeterminate ? <FaMinus /> : null}
                        />
                      );
                    })()}
                  </div>

                  {/* Virtualized List */}
                  <div
                    style={{
                      height: Math.min(
                        groupedRetailers[typeKey].length * 60,
                        400
                      ),
                    }}
                  >
                    <AutoSizer>
                      {({ height, width }) => (
                        <FixedSizeList
                          className='List w-full'
                          height={height}
                          itemCount={Math.ceil(
                            groupedRetailers[typeKey].length / 2
                          )}
                          itemSize={60}
                          width={width}
                          innerElementType={({ children, ...props }) => (
                            <div {...props} className='flex flex-col gap-2'>
                              {children}
                            </div>
                          )}
                        >
                          {({ index, style }) => {
                            const startIdx = index * 2;
                            const retailerRow = groupedRetailers[typeKey].slice(
                              startIdx,
                              startIdx + 2
                            );

                            return (
                              <div style={{ ...style }}>
                                <div className='flex gap-4'>
                                  {retailerRow.map((retailer) => (
                                    <RetailerRowItem
                                      key={retailer._id}
                                      retailer={retailer}
                                    />
                                  ))}
                                  {retailerRow.length < 2 && (
                                    <div className='flex-1' />
                                  )}
                                </div>
                              </div>
                            );
                          }}
                        </FixedSizeList>
                      )}
                    </AutoSizer>
                  </div>
                </div>
              )
          )}
        </div>

        {/* Sticky Save Button */}
        <div className='fixed bottom-0 left-64 right-4 bg-white border-t z-50 flex justify-end p-4'>
          <Button className='m-2' onClick={handleSaveSchemeAccess}>
            Save Access
          </Button>
        </div>
      </PageContent>
    </>
  );
};

export default CampaignAllowAccess;
